import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config/apiConfig.js';
import { useAuth } from '../../components/router/authContext.jsx';
import { useScrollAnimation } from '../../hooks/useScrollAnimation.js';
import DOMPurify from 'dompurify';
import { Link } from 'react-router-dom';
import { Notification } from '../../components/pro_calculator/general/Notification.jsx';

axios.defaults.withCredentials = true;

function LoginLanding() {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');

    const [newUserUsername, setNewUserUsername] = useState('');
    const [newUserEmail, setNewUserEmail] = useState('');
    const [newUserPassword, setNewUserPassword] = useState('');
    const [newUserConfirmPassword, setNewUserConfirmPassword] = useState('');
    const [newUserFirstName, setNewUserFirstName] = useState('');
    const [newUserLastName, setNewUserLastName] = useState('');
    const [newUserCompanyName, setNewUserCompanyName] = useState('');
    const [newUserPhoneNumber, setNewUserPhoneNumber] = useState('');
    const [newUserTermsAndConditions, setNewUserTermsAndConditions] = useState(false);
    const [showTermsModal, setShowTermsModal] = useState(false);
    const [termsContent, setTermsContent] = useState('');

    // Notification
    const [showNotification, setShowNotification] = useState(false);
    const [notificationMessage, setNotificationMessage] = useState('');

    const [error, setError] = useState('');
    const [status, setStatus] = useState('');
    const [loginState, setLoginState] = useState('login');
    const [email, setEmail] = useState('');

    // ACT Free Trial Token
    const [freeTrialToken, setFreeTrialToken] = useState('');
    const apiCallInProgress = useRef(false);
    const tokenValidationComplete = useRef(false);

    // Loading states
    const [loading, setLoading] = useState(false);
    const [tokenValidated, setTokenValidated] = useState(false);
    const [formSubmitting, setFormSubmitting] = useState(false);

    const errorTimeoutRef = useRef(null);

    const navigate = useNavigate();
    const location = useLocation();
    const { login, isAuthenticated, isLoading } = useAuth();

    // Animation hook for form entry
    const [formRef, isFormVisible] = useScrollAnimation({ threshold: 0.3 });

    const clearError = () => {
        setError('');
        if (errorTimeoutRef.current) {
            clearTimeout(errorTimeoutRef.current);
            errorTimeoutRef.current = null;
        }
    };

    const validateFreeTrialToken = async (token) => {
        if (apiCallInProgress.current || tokenValidationComplete.current || !token) {
            return;
        }

        apiCallInProgress.current = true;

        try {
            setLoading(true);
            const response = await axios.post(API_ENDPOINTS.tokenValidationACT, {
                token: freeTrialToken,
            });

            console.log("Response:", response.data);
            if (response.data.email) {
                setTokenValidated(true);
                console.log("Email filled", response.data.email);
                setNewUserEmail(response.data.email);
            } else {
                setError("This invitation link is invalid or has expired.");
            }
        } catch (err) {
            setError("Unable to validate your invitation. Please try again later.");
            console.error("Token validation error:", err);
        } finally {
            setLoading(false);
        }
    };

    // Check for free trial token
    useEffect(() => {
        if (tokenValidationComplete.current) {
            return;
        }

        console.log("Checking for free trial token");
        const queryParams = new URLSearchParams(location.search);
        const token = queryParams.get('token');
        console.log("Token:", token);
        if (token && !apiCallInProgress.current) {
            setLoginState('register');
            setFreeTrialToken(token);
            console.log("freeTrialToken:", freeTrialToken);
            validateFreeTrialToken(freeTrialToken);
        }
    }, [location, freeTrialToken]);

    useEffect(() => {
        if (error) {
            if (errorTimeoutRef.current) {
                clearTimeout(errorTimeoutRef.current);
            }

            errorTimeoutRef.current = setTimeout(() => {
                setError('');
                errorTimeoutRef.current = null;
            }, 15000);
        }

        return () => {
            if (errorTimeoutRef.current) {
                clearTimeout(errorTimeoutRef.current);
            }
        };
    }, [error]);

    // Terms and conditions
    useEffect(() => {
        if (showTermsModal) {
            fetch('/terms-and-conditions.html')
                .then(response => response.text())
                .then(html => {
                    const sanitizedHtml = DOMPurify.sanitize(`<div>${html}</div>`);
                    setTermsContent(sanitizedHtml);
                    console.log("Fetched and sanitized terms and conditions");
                })
                .catch((error) => {
                    console.error('Error fetching terms', error);
                })
        }
    }, [showTermsModal]);

    useEffect(() => {
        const handleUserInteraction = () => {
            if (error) {
                clearError();
            }
        };

        document.addEventListener('click', handleUserInteraction);
        document.addEventListener('keydown', handleUserInteraction);

        return () => {
            document.removeEventListener('click', handleUserInteraction);
            document.removeEventListener('keydown', handleUserInteraction);
        };
    }, [error]);

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const mode = queryParams.get('mode');
        const redirect = queryParams.get('redirect');

        if (mode === 'register') {
            setLoginState('register');
        }
        if (redirect === 'true') {
            setShowNotification(true);
            setNotificationMessage('Please login to continue.');
        }
    }, [location]);

    const handleLogin = async (e) => {
        e.preventDefault();
        setFormSubmitting(true);
        console.log('Login attempt initiated for username:', username);
        console.log('Full login URL:', API_ENDPOINTS.login);

        try {
            console.log('Making login request to:', API_ENDPOINTS.login);
            const response = await axios.post(API_ENDPOINTS.login, {
                username,
                password
            });
            console.log('Login successful, received token:', response.data.token ? 'Token exists' : 'No token received');

            localStorage.setItem('authToken', response.data.token);

            await login({
                token: response.data.token,
                user: response.data.user || { username }
            });

            setError('');

            if (response.data.subscription_status === "active") {
                navigate('/fleet-planning-pro');
            } else {
                navigate('/solutions');
            }
        } catch (error) {
            console.error('Login failed:', error.response?.data || error.message);
            setError('Invalid username or password');
        } finally {
            setFormSubmitting(false);
        }
    }

    const handleRegister = async (e) => {
        e.preventDefault();
        setFormSubmitting(true);
        console.log('Register attempt initiated for username:', newUserUsername);
        console.log('Full register URL:', API_ENDPOINTS.register);

        try {
            if (newUserPassword !== newUserConfirmPassword) {
                setError('Passwords do not match');
                return;
            }

            const response = await axios.post(API_ENDPOINTS.register, {
                username: newUserUsername,
                email: newUserEmail,
                password: newUserPassword,
                confirm_password: newUserConfirmPassword,
                first_name: newUserFirstName,
                last_name: newUserLastName,
                company_name: newUserCompanyName,
                phone_number: newUserPhoneNumber,
                token: freeTrialToken,
                has_token: tokenValidated
            });
            console.log('Registration successful, received token:', response.data.token ? 'Token exists' : 'No token received');

            localStorage.setItem('authToken', response.data.token);
            setError('');
            console.log('Navigating to home page');
            if (tokenValidated) {
                navigate('/fleet-planning-pro');
            } else {
                setLoginState('register-success');
            }
        } catch (error) {
            console.error('Registration failed:', error.response?.data || error.message);

            if (error.response && error.response.data && error.response.data.error) {
                setError(error.response.data.error);
            } else {
                setError('Registration failed. Please try again later.');
            }
        } finally {
            setFormSubmitting(false);
        }
    }

    const handleForgotPassword = async (e) => {
        e.preventDefault();
        setFormSubmitting(true);
        console.log('Forgot password attempt initiated for email:', email);

        try {
            const response = await axios.post(API_ENDPOINTS.forgotPassword, {
                email
            });
            console.log('Password reset email sent successfully');
            setStatus('Password reset email sent successfully, please check your email for a link to reset your password.');
        } catch (error) {
            console.error('Password reset failed:', error.response?.data || error.message);
            setError('Failed to send password reset email');
        } finally {
            setFormSubmitting(false);
        }
    }

    // Loading Spinner Component
    const LoadingSpinner = () => (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-8 flex flex-col items-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
                <p className="text-gray-600 font-nunito">Loading...</p>
            </div>
        </div>
    );

    const renderLoginForm = () => {
        switch (loginState) {
            case 'login':
                return (
                    <div className="w-full flex justify-center items-center p-8">
                        <div className="w-full max-w-2xl bg-white rounded-lg p-8 shadow-lg">
                            {/* Header - Centered */}
                            <div className="text-center mb-8">
                                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 mb-2 font-nunito">Welcome Back</h2>
                                <p className="text-sm sm:text-sm md:text-lg text-gray-600 font-nunito">Sign in to your account</p>
                            </div>

                            <form onSubmit={handleLogin} className="space-y-6">
                                {/* Email Input */}
                                <div className="text-center">
                                    <label htmlFor="email" className="block mb-2 text-gray-800 font-nunito font-medium text-sm text-left">
                                        Email or Username
                                    </label>
                                    <input
                                        type="text"
                                        className="w-full px-4 py-3 rounded-lg bg-gray-100 border-none font-nunito focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200 text-left"
                                        id="email"
                                        onChange={(e) => setUsername(e.target.value)}
                                        required
                                        disabled={formSubmitting}
                                    />
                                </div>

                                {/* Password Input */}
                                <div className="text-center">
                                    <label htmlFor="password" className="block mb-2 text-gray-800 font-nunito font-medium text-sm text-left">
                                        Password
                                    </label>
                                    <input
                                        type="password"
                                        className="w-full px-4 py-3 rounded-lg bg-gray-100 border-none font-nunito focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200 text-left"
                                        id="password"
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        disabled={formSubmitting}
                                    />
                                </div>

                                {/* Login Button - Centered */}
                                <div className="flex justify-center">
                                    <button
                                        type="submit"
                                        className="w-full font-nunito uppercase text-white bg-gradient-to-r from-primary to-primary-light py-3 px-8 tracking-wider text-sm font-medium rounded-lg border-0 transition-all duration-500 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                                        disabled={formSubmitting}
                                    >
                                        {formSubmitting ? (
                                            <>
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                                Logging in...
                                            </>
                                        ) : (
                                            'Login'
                                        )}
                                    </button>
                                </div>
                            </form>

                            {/* Error Message - Centered */}
                            {error && (
                                <div className="text-center mt-4">
                                    <p className="text-red-600 text-sm font-nunito">{error}</p>
                                </div>
                            )}

                            {/* Forgot Password Link - Centered */}
                            <div className="text-center mt-6">
                                <button
                                    className="font-nunito bg-transparent border-none text-blue-500 underline cursor-pointer py-2 px-0 text-sm hover:text-blue-600 disabled:opacity-50 transition-colors duration-200"
                                    onClick={() => setLoginState('forgot')}
                                    disabled={formSubmitting}
                                >
                                    Forgot your password?
                                </button>
                            </div>

                            {/* Sign Up Section - Centered */}
                            <div className="text-center mt-6 pt-4 border-t border-gray-200">
                                <div className="flex flex-col items-center space-y-2">
                                    <span className="text-gray-500 font-nunito text-sm">
                                        Don't have an account?
                                    </span>
                                    <button
                                        className="font-nunito bg-transparent border-none text-blue-500 underline cursor-pointer text-sm hover:text-blue-600 disabled:opacity-50 transition-colors duration-200"
                                        onClick={() => setLoginState('register')}
                                        disabled={formSubmitting}
                                    >
                                        Sign up now
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            case 'register':
                return (
                    <div className="w-full flex justify-center items-center p-4">
                        <div className="w-full max-w-2xl bg-white rounded-lg p-4 shadow-lg">
                            {/* Header - Centered */}
                            <div className="text-center mb-4">
                                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 mb-1 font-nunito">Create Account</h2>
                                <p className="text-sm sm:text-sm md:text-lg text-gray-600 font-nunito">Join us today</p>
                            </div>

                            <form onSubmit={handleRegister} className="space-y-2">
                                {/* Name Fields */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="text-center">
                                        <label htmlFor="first-name" className="block mb-1 text-gray-800 font-nunito font-medium text-sm text-left">
                                            First Name <span className="text-red-600">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className="w-full px-3 py-2 rounded-lg bg-gray-100 border-none font-nunito focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200 text-left text-sm"
                                            id="first-name"
                                            onChange={(e) => setNewUserFirstName(e.target.value)}
                                            required
                                            disabled={formSubmitting}
                                        />
                                    </div>
                                    <div className="text-center">
                                        <label htmlFor="last-name" className="block mb-1 text-gray-800 font-nunito font-medium text-sm text-left">
                                            Last Name <span className="text-red-600">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className="w-full px-3 py-2 rounded-lg bg-gray-100 border-none font-nunito focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200 text-left text-sm"
                                            id="last-name"
                                            onChange={(e) => setNewUserLastName(e.target.value)}
                                            required
                                            disabled={formSubmitting}
                                        />
                                    </div>
                                </div>

                                {/* Username */}
                                <div className="text-center">
                                    <label htmlFor="username" className="block mb-1 text-gray-800 font-nunito font-medium text-sm text-left">
                                        Username <span className="text-red-600">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="w-full px-3 py-2 rounded-lg bg-gray-100 border-none font-nunito focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200 text-left text-sm"
                                        id="username"
                                        onChange={(e) => setNewUserUsername(e.target.value)}
                                        required
                                        disabled={formSubmitting}
                                    />
                                </div>

                                {/* Email */}
                                <div className="text-center">
                                    <label htmlFor="email" className="block mb-1 text-gray-800 font-nunito font-medium text-sm text-left">
                                        Email <span className="text-red-600">*</span>
                                    </label>
                                    {tokenValidated ? (
                                        <div className="w-full px-3 py-2 rounded-lg bg-gray-200 font-nunito text-center text-sm text-gray-600">
                                            {newUserEmail}
                                        </div>
                                    ) : (
                                        <input
                                            type="email"
                                            className="w-full px-3 py-2 rounded-lg bg-gray-100 border-none font-nunito focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200 text-left text-sm"
                                            id="email"
                                            onChange={(e) => setNewUserEmail(e.target.value)}
                                            required
                                            disabled={formSubmitting}
                                        />
                                    )}
                                </div>

                                {/* Password Fields */}
                                <div className="text-center">
                                    <label htmlFor="password" className="block mb-1 text-gray-800 font-nunito font-medium text-sm text-left">
                                        Password <span className="text-red-600">*</span>
                                    </label>
                                    <input
                                        type="password"
                                        className="w-full px-3 py-2 rounded-lg bg-gray-100 border-none font-nunito focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200 text-left text-sm"
                                        id="password"
                                        onChange={(e) => setNewUserPassword(e.target.value)}
                                        required
                                        disabled={formSubmitting}
                                    />
                                </div>

                                <div className="text-center">
                                    <label htmlFor="confirm-password" className="block mb-1 text-gray-800 font-nunito font-medium text-sm text-left">
                                        Confirm Password <span className="text-red-600">*</span>
                                    </label>
                                    <input
                                        type="password"
                                        className="w-full px-3 py-2 rounded-lg bg-gray-100 border-none font-nunito focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200 text-left text-sm"
                                        id="confirm-password"
                                        onChange={(e) => setNewUserConfirmPassword(e.target.value)}
                                        required
                                        disabled={formSubmitting}
                                    />
                                </div>

                                {/* Optional Fields */}
                                <div className="text-center">
                                    <label htmlFor="company-name" className="block mb-1 text-gray-800 font-nunito font-medium text-sm text-left">
                                        Company Name
                                    </label>
                                    <input
                                        type="text"
                                        className="w-full px-3 py-2 rounded-lg bg-gray-100 border-none font-nunito focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200 text-left text-sm"
                                        id="company-name"
                                        onChange={(e) => setNewUserCompanyName(e.target.value)}
                                        disabled={formSubmitting}
                                    />
                                </div>

                                <div className="text-center">
                                    <label htmlFor="phone-number" className="block mb-1 text-gray-800 font-nunito font-medium text-sm text-left">
                                        Phone Number
                                    </label>
                                    <input
                                        type="text"
                                        className="w-full px-3 py-2 rounded-lg bg-gray-100 border-none font-nunito focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200 text-left text-sm"
                                        id="phone-number"
                                        onChange={(e) => setNewUserPhoneNumber(e.target.value)}
                                        disabled={formSubmitting}
                                    />
                                </div>

                                {/* Required Fields Note - Centered */}
                                <div className="text-center text-xs text-gray-500 mt-2">
                                    <span className="text-red-600 font-bold">*</span> Required fields
                                </div>

                                {/* Terms and Conditions - Centered */}
                                <div className="flex justify-center items-center mt-2">
                                    <label htmlFor="terms-and-conditions" className="flex items-center text-center">
                                        <input
                                            type="checkbox"
                                            id="terms-and-conditions"
                                            onChange={(e) => setNewUserTermsAndConditions(e.target.checked)}
                                            className="mr-2"
                                            disabled={formSubmitting}
                                        />
                                        <span className="font-nunito text-sm">I agree to the{' '}</span>
                                        <span
                                            onClick={() => setShowTermsModal(true)}
                                            className="text-blue-500 font-nunito underline cursor-pointer hover:text-blue-600 text-sm ml-1"
                                        >
                                            Terms and Conditions
                                        </span>
                                    </label>
                                </div>

                                {/* Terms Modal */}
                                {showTermsModal && (
                                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
                                        <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
                                            {/* Modal Header */}
                                            <div className="flex justify-between items-center p-6 border-b border-gray-200">
                                                <h3 className="text-xl font-bold text-gray-800 font-nunito">Terms and Conditions</h3>
                                                <button
                                                    onClick={() => setShowTermsModal(false)}
                                                    className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                                                >
                                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                                                    </svg>
                                                </button>
                                            </div>

                                            {/* Modal Body - Scrollable Content */}
                                            <div className="flex-1 overflow-y-auto p-6">
                                                <div
                                                    dangerouslySetInnerHTML={{ __html: termsContent }}
                                                    className="prose prose-sm max-w-none font-nunito"
                                                />
                                            </div>

                                            {/* Modal Footer */}
                                            <div className="flex justify-center p-6 border-t border-gray-200 bg-gray-50">
                                                <button
                                                    onClick={() => setShowTermsModal(false)}
                                                    className="btn-primary px-8"
                                                >
                                                    Close
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Submit Button - Centered */}
                                <div className="flex justify-center mt-3">
                                    <button
                                        type="submit"
                                        className="w-full font-nunito uppercase text-white bg-gradient-to-r from-primary to-primary-light py-2 px-6 tracking-wider text-sm font-medium rounded-lg border-0 transition-all duration-500 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                                        disabled={!newUserTermsAndConditions || formSubmitting}
                                    >
                                        {formSubmitting ? (
                                            <>
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                                Creating Account...
                                            </>
                                        ) : (
                                            'Create Account'
                                        )}
                                    </button>
                                </div>
                            </form>

                            {/* Error Message - Centered */}
                            {error && (
                                <div className="text-center mt-2">
                                    <p className="text-red-600 text-sm font-nunito">{error}</p>
                                </div>
                            )}

                            {/* Back to Login - Centered */}
                            <div className="text-center mt-3 pt-2 border-t border-gray-200">
                                <div className="flex flex-col items-center space-y-1">
                                    <span className="text-gray-500 font-nunito text-sm">
                                        Already have an account?
                                    </span>
                                    <button
                                        className="font-nunito bg-transparent border-none text-blue-500 underline cursor-pointer text-sm hover:text-blue-600 disabled:opacity-50 transition-colors duration-200"
                                        onClick={() => setLoginState('login')}
                                        disabled={formSubmitting}
                                    >
                                        Back to login
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            case 'register-success':
                return (
                    <div className="w-full flex justify-center items-center p-8">
                        <div className="w-full max-w-2xl bg-white rounded-lg p-8 shadow-lg">
                            <div className="text-center">
                                <div className="mb-6">
                                    <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 mb-2 font-nunito">Success!</h2>
                                </div>
                                <p className="text-sm sm:text-base text-gray-600 font-nunito text-center leading-relaxed">
                                    Registration successful! Please check your email for a verification link. You can now close this tab.
                                </p>
                            </div>
                        </div>
                    </div>
                );
            case 'forgot':
                return (
                    <div className="w-full flex justify-center items-center p-8">
                        <div className="w-full max-w-2xl bg-white rounded-lg p-8 shadow-lg">
                            {/* Header - Centered */}
                            <div className="text-center mb-8">
                                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 mb-2 font-nunito">Reset Password</h2>
                                <p className="text-sm sm:text-base text-gray-600 font-nunito text-center leading-relaxed">
                                    Enter your email below and we'll send you a link to reset your password.
                                </p>
                            </div>

                            <form onSubmit={handleForgotPassword} className="space-y-6">
                                {/* Email Input */}
                                <div className="text-center">
                                    <label htmlFor="email" className="block mb-2 text-gray-800 font-nunito font-medium text-sm text-left">
                                        Email
                                    </label>
                                    <input
                                        type="email"
                                        className="w-full px-4 py-3 rounded-lg bg-gray-100 border-none font-nunito focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200 text-center"
                                        id="email"
                                        placeholder="e.g. johndoe@gmail.com"
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        disabled={formSubmitting}
                                    />
                                </div>

                                {/* Submit Button - Centered */}
                                <div className="flex justify-center">
                                    <button
                                        type="submit"
                                        className="w-full font-nunito uppercase text-white bg-gradient-to-r from-primary to-primary-light py-3 px-8 tracking-wider text-sm font-medium rounded-lg border-0 transition-all duration-500 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                                        disabled={formSubmitting}
                                    >
                                        {formSubmitting ? (
                                            <>
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                                Sending...
                                            </>
                                        ) : (
                                            'Reset Password'
                                        )}
                                    </button>
                                </div>
                            </form>

                            {/* Error/Status Messages - Centered */}
                            {error && (
                                <div className="text-center mt-4">
                                    <p className="text-red-600 text-sm font-nunito">{error}</p>
                                </div>
                            )}
                            {status && (
                                <div className="text-center mt-4">
                                    <p className="text-green-600 text-sm font-nunito">{status}</p>
                                </div>
                            )}

                            {/* Back to Login - Centered */}
                            <div className="text-center mt-6 pt-4 border-t border-gray-200">
                                <button
                                    className="font-nunito bg-transparent border-none text-blue-500 underline cursor-pointer text-sm hover:text-blue-600 disabled:opacity-50 transition-colors duration-200"
                                    onClick={() => setLoginState('login')}
                                    disabled={formSubmitting}
                                >
                                    Back to login
                                </button>
                            </div>
                        </div>
                    </div>
                );
            default:
                return null;
        };
    }

    // Show loading spinner during auth loading or token validation
    if (isLoading || loading) {
        return <LoadingSpinner />;
    }

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col relative">
            {/* Logo in top left corner */}
            <div className={`absolute top-6 left-6 z-10 transition-all duration-1000 ease-out transform ${isFormVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}>
                <Link to="/">
                    <img src="/img/logo.png" alt="AmpTrans Logo" className="w-auto h-12" />
                </Link>
            </div>

            {/* Notification */}
            {showNotification && (
                <Notification
                    message={notificationMessage}
                    type="info"
                    visible={showNotification}
                    onClose={() => setShowNotification(false)}
                />
            )}

            {/* Main content container - centered */}
            <div className="flex-1 flex items-center justify-center px-4 py-8">
                <div
                    ref={formRef}
                    className={`w-full max-w-2xl transition-all duration-1000 ease-out transform ${isFormVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                        }`}
                >
                    {renderLoginForm()}
                </div>
            </div>
        </div>
    );
}

export default LoginLanding;